import { useMemo, useState } from "react";
import { type LoginCallDTO } from "../dto";
import { Card } from "../../Configurators/Linear/components/Card";
import { Divider } from "../../common/components/Divider";
import classNames from "classnames";
import { MediasLogo, type MediasVariant } from "../../Header/components/TopBar/MediasLogo";
import { Button } from "../../common/components/Button/Button";
import { LinkButton } from "../../common/components/Link";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { useIsMinDesktopSize } from "../../common/hooks/useMediaQuery";
import { REGISTER_PAGE } from "../../utils/url/staticUrls";
import { useBooleanState } from "../../common/hooks/useBooleanState";
import { isForcedLogin } from "../../Login/loginUtils";
import LoginPopup from "../../Login/components/LoginPopup";
import ResetPasswordPopup from "../../Login/components/ResetPasswordPopup";
import { createMarkup } from "./CMSSection";
import { userIsLoggedIn } from "../../utils/roles";
import { useTranslation } from "react-i18next";

export interface CMSLoginCallProps extends Omit<LoginCallDTO, "type"> {
  className?: string;
}

export const CMSLoginCall = (props: CMSLoginCallProps) => {
  const mediasLogoVariant: MediasVariant = props.accessLevel.toLowerCase() as MediasVariant;
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const { val: loginPopupOpen, setTrue: openLogin, setFalse: closeLogin } = useBooleanState(false);
  const { val: resetPopupOpen, setTrue: openReset, setFalse: closeReset } = useBooleanState(false);
  const [email, setEmail] = useState("");

  const onResetClick = (email: string) => {
    setEmail(email);
    closeLogin();
    openReset();
  };

  const content = useMemo(() => createMarkup(props.content), [props.content]);

  if (userIsLoggedIn()) {
    return null;
  }

  return (
    <Card className={classNames(props.className, "scha__login-call")} disableHover={true}>
      <MediasLogo className={"scha__login-call__logo"} variant={mediasLogoVariant} />
      <Divider orientation={isDesktop ? "vertical" : "horizontal"} flexItem={true} />
      <div className={"scha__login-call__content"} dangerouslySetInnerHTML={content} />
      <div className={"scha__login-call__actions"}>
        <Button
          data-test-id={"register-free"}
          href={REGISTER_PAGE}
          className={"scha__login-call__register button"}
          size={"medium"}
          startIcon={<Icon type={IconType.User} />}
        >
          {t("page.registerFree.register")}
        </Button>
        <span>{t("common.or")} </span>
        <LinkButton data-test-id={"sign-in"} onClick={openLogin}>
          {t("header.link.signIn")}
        </LinkButton>
      </div>
      {(loginPopupOpen || (isForcedLogin() && !resetPopupOpen)) && (
        <LoginPopup initialEmail={""} onResetClick={onResetClick} close={closeLogin} />
      )}
      {resetPopupOpen && <ResetPasswordPopup initialEmail={email} close={closeReset} />}
    </Card>
  );
};
